import { DateTime, Exercise, ExerciseMetric, CircuitMetric, FireStoreMeasurement, Variants, Leaderboard } from "types";
import * as Components from "./LeaderboardTablePercentage.components";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import useAllowedAthleteMeasurementFilter from "hooks/useAllowedAthleteMeasurementFilter";
import useMeasurementsFilter from "features/Live/hooks/useMeasurementsFilter";
import { useLoadingContext } from "components/LocalisedLoadingProvider";
import LeaderboardService from "services/LeaderboardService";
import useOrganisation from "contexts/OrganisationContext/useOrganisation";
import Loader from "components/Loader/Loader";
import MessageWithLogo from "components/MessageWithLogo";
import { LeaderboardComparePeriod } from "features/Leaderboards/LeaderboardTables/LeaderboardEditable/LeaderboardEditable.types";
import useTickerQueue from "components/Ticker/TickerProvider/useTickerQueue";
import usePercentageRankings from "./hooks/usePercentageRankings";
import LeaderboardHelpers from "features/Leaderboards/helpers/LeaderboardHelpers";
import LeaderboardTickerPortal from "../LeaderboardTickerPortal";
import useUpdateLeaderboardStore from "../hooks/useUpdateLeaderboardStore";
import { useTranslation } from "react-i18next";
import useHyroxMetadata from "providers/HyroxMetadataProvider/useHyroxMetadata";
interface LeaderboardTablePercentageProps {
  // Legacy props (optional for backward compatibility)
  exercise?: Exercise;
  metric?: ExerciseMetric | CircuitMetric;
  variant?: Variants | null;
  // New props
  leaderboard?: Leaderboard;
  timePeriodStartDate?: string;
  startDate?: string;
  endDate?: string;
  comparePeriodType: LeaderboardComparePeriod;
  startDateComparePeriod: DateTime | null;
}

const LeaderboardTablePercentage = memo(function LeaderboardTablePercentage(props: LeaderboardTablePercentageProps) {
  const { t } = useTranslation();
  const { timePeriodStartDate, startDate, endDate, exercise, metric, variant, leaderboard } = props;
  const organisation = useOrganisation();
  const hyroxMetadata = useHyroxMetadata();
  const [loading, setLoading] = useLoadingContext();
  const [measurements, setMeasurements] = useState<{ [id: string]: FireStoreMeasurement | undefined }>({});
  const [queue, setQueue] = useTickerQueue<FireStoreMeasurement>();

  // Support both Score and Exercise types
  const isScoreType = leaderboard?.measureType === "Score";
  const exerciseId = exercise?.id;
  const metricField = isScoreType ? "score" : metric?.field;
  const sortOrder = isScoreType ? 1 : (metric?.sortType ?? 1); // Score: descending (1)

  // Get scoreMetadata for Score type leaderboards
  const scoreMetadata =
    isScoreType && leaderboard?.metricId
      ? hyroxMetadata.find((score) => score.field === leaderboard.metricId)
      : undefined;

  const initialMeasurementsRef = useRef<boolean>(true);
  const tickerMeasurement: FireStoreMeasurement | undefined = queue[0];

  const updateMeasurementStore = useUpdateLeaderboardStore({
    initialMeasurementsRef,
    setMeasurements,
    setQueue,
  });

  useEffect(() => {
    if (!metricField) return;

    setLoading(true);

    // Use provided leaderboard or create temporary one from legacy props
    const subscriptionLeaderboard: Leaderboard = leaderboard || {
      measureType: "Exercise",
      exerciseId: exerciseId || null,
      metricId: metricField || "",
      variant: variant ?? null,
      id: "",
      name: "",
      groups: [],
      groupIds: [],
      type: "Day",
      comparePeriodType: null,
      rankType: undefined,
      startTime: null,
      endTime: null,
      startDateComparePeriod: null,
    };

    const unsubscribe = LeaderboardService.subscribe(
      organisation.id,
      subscriptionLeaderboard,
      startDate!,
      endDate!,
      (snapshot) => {
        LeaderboardHelpers.onSnapshot(snapshot, updateMeasurementStore, { metricField });

        if (!snapshot.metadata.fromCache) {
          initialMeasurementsRef.current = false;
        }

        setLoading(false);
      }
    );

    return () => {
      initialMeasurementsRef.current = true;
      setMeasurements({});
      setQueue([]);
      unsubscribe();
      setLoading(false);
    };
  }, [
    endDate,
    exerciseId,
    leaderboard,
    metricField,
    organisation.id,
    setLoading,
    setQueue,
    sortOrder,
    startDate,
    updateMeasurementStore,
    variant,
  ]);

  const percentageRankings = usePercentageRankings(
    measurements,
    metricField!,
    sortOrder!,
    variant ?? null,
    timePeriodStartDate || null
  );
  const allowedAthleteMeasurements = useAllowedAthleteMeasurementFilter(percentageRankings.measurements);
  const filteredMeasurements = useMeasurementsFilter(allowedAthleteMeasurements);

  const allowedTickerMeasurements = useAllowedAthleteMeasurementFilter(
    useMemo(() => [tickerMeasurement].filter(Boolean), [tickerMeasurement])
  );
  const filteredTickerMeasurements = useMeasurementsFilter(allowedTickerMeasurements);
  const filteredTickerMeasurement = useMemo(() => filteredTickerMeasurements.shift(), [filteredTickerMeasurements]);

  useEffect(() => {
    // if a ticker measurement exists but should not be shown bump the queue up 1
    if (tickerMeasurement && typeof filteredTickerMeasurement === "undefined") {
      setQueue((prevQueue) => prevQueue.slice(1));
    }
  }, [filteredTickerMeasurement, setQueue, tickerMeasurement]);

  if (loading && filteredMeasurements.length === 0) {
    return <Loader isSmall />;
  }

  if (filteredMeasurements.length === 0) {
    return <MessageWithLogo>{t("leaderboard.no_measurements_error")}</MessageWithLogo>;
  }

  return (
    <>
      {filteredMeasurements.map((rankedMeasurement, index) => (
        <Components.LeaderboardTablePercentageRow
          key={rankedMeasurement.id}
          position={index + 1}
          measurement={rankedMeasurement}
          comparativeMeasurement={percentageRankings.getComparative(rankedMeasurement.athleteId)}
          metric={metric}
          scoreMetadata={scoreMetadata}
          metricField={metricField}
          sortOrder={sortOrder}
          comparePeriodType={props.comparePeriodType}
          startDateComparePeriod={props.startDateComparePeriod}
        />
      ))}
      <LeaderboardTickerPortal measurementId={filteredTickerMeasurement?.id}>
        {filteredTickerMeasurement && (
          <Components.LeaderboardTablePercentageRow
            key={filteredTickerMeasurement.id}
            position={filteredMeasurements.indexOf(filteredTickerMeasurement) + 1 || null}
            measurement={filteredTickerMeasurement}
            comparativeMeasurement={percentageRankings.getComparative(filteredTickerMeasurement.athleteId)}
            metric={metric}
            scoreMetadata={scoreMetadata}
            metricField={metricField}
            sortOrder={sortOrder}
            comparePeriodType={props.comparePeriodType}
            startDateComparePeriod={props.startDateComparePeriod}
          />
        )}
      </LeaderboardTickerPortal>
    </>
  );
});

export default LeaderboardTablePercentage;
